// Notes on a call: the Notes section of the call detail page (list, add, edit, delete, author and time)
// and the note field on the live call card. Notes follow the call's visibility; authors can change their
// own note for 15 minutes, after which a follow-up note is the way to correct one.
import type { Call } from '@bcio-voice/contracts';
import { type FormEvent, type KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NotebookPen, Pencil, Trash2 } from 'lucide-react';
import { Icon } from '../../ui/Icon.tsx';
import { SkeletonRows, StatePanel } from '../../ui/StatePanel.tsx';
import { useAnnouncer } from '../../ui/Announcer.tsx';
import { useWorkspace } from '../../state/workspace.tsx';
import { type CallNote, CallNotesClient, isAbortError, isApiRequestError } from '../../lib/api.ts';
import { formatAbsolute, formatClock } from '../../lib/format.ts';
import { BlocklistInlineStyles } from '../blocklist/InlineStyles.tsx';
import { DRAFT_SAVE_MS, NOTE_MAX, canChangeNote, draftKey, noteErrorText, readNote, readNotes, sessionDrafts, wasEdited } from './notes.ts';

const live = (call: Call) => call.state !== 'ended' && call.state !== 'failed';
const submitKey = (e: KeyboardEvent) => e.key === 'Enter' && (e.ctrlKey || e.metaKey);

type ListState =
  | { status: 'loading' }
  | { status: 'ready'; notes: CallNote[] }
  | { status: 'denied' }
  | { status: 'unavailable' }
  | { status: 'error'; message: string };

/**
 * The Notes section of a call. `onChanged` reports the list after every load and change; `mutated` is
 * true for a change made here, so the page can refresh the call's timeline (notes appear there too).
 */
export function CallNotesSection({ call, onChanged }: { call: Call; onChanged?: (notes: CallNote[], mutated: boolean) => void }) {
  const ws = useWorkspace();
  const { announce } = useAnnouncer();
  const client = useMemo(() => new CallNotesClient(ws.api), [ws.api]);
  const drafts = useMemo(() => sessionDrafts(), []);
  const key = draftKey(ws.session.tenant?.id, ws.userId, call.id);
  const tz = ws.config.value?.tenant_timezone;
  const [list, setList] = useState<ListState>({ status: 'loading' });
  const [tick, setTick] = useState(0);
  // A note started on the live call card and not saved yet. While the call is live the card owns it.
  const [carried, setCarried] = useState(() => (live(call) ? null : drafts.get(key)));
  const [text, setText] = useState(() => carried ?? '');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const changed = useRef(onChanged);
  changed.current = onChanged;

  useEffect(() => {
    const controller = new AbortController();
    setList(l => (l.status === 'ready' ? l : { status: 'loading' }));
    client.list(call.id, controller.signal).then(
      body => {
        const notes = readNotes(body);
        if (!notes) { setList({ status: 'error', message: 'The server sent notes this version can’t read.' }); return; }
        setList({ status: 'ready', notes });
        changed.current?.(notes, false);
      },
      error => {
        if (isAbortError(error)) return;
        // A 404 here means a server without notes yet, or a call whose notes aren't shared with this person.
        if (isApiRequestError(error) && error.status === 403 && error.code !== 'mfa_required') setList({ status: 'denied' });
        else if (isApiRequestError(error) && (error.status === 404 || error.status === 501)) setList({ status: 'unavailable' });
        else setList({ status: 'error', message: noteErrorText(error, 'Couldn’t load notes') });
      },
    );
    return () => controller.abort();
  }, [client, call.id, tick]);

  const replace = useCallback((next: CallNote[]) => {
    setList({ status: 'ready', notes: next });
    changed.current?.(next, true);
  }, []);

  const add = async (event?: FormEvent) => {
    event?.preventDefault();
    const body = text.trim();
    if (!body || adding) return;
    setAdding(true);
    setAddError(null);
    try {
      const note = readNote(await client.add(call.id, body));
      setText('');
      setCarried(null);
      drafts.remove(key);
      announce('Note added');
      if (note && list.status === 'ready') replace([...list.notes, note]);
      else setTick(t => t + 1);
    } catch (error) {
      setAddError(noteErrorText(error, 'Couldn’t add this note'));
    } finally {
      setAdding(false);
    }
  };

  const discardCarried = () => {
    drafts.remove(key);
    setCarried(null);
    setText('');
    announce('Draft discarded');
  };

  return (
    <section className="detail-section" aria-labelledby={`notes-title-${call.id}`} id="call-notes">
      <BlocklistInlineStyles />
      <h3 id={`notes-title-${call.id}`} className="section-title">Notes</h3>
      {list.status === 'loading' ? <SkeletonRows rows={1} label="Loading notes" /> : null}
      {list.status === 'denied' ? <StatePanel variant="denied" compact headingLevel={3} title="You can’t see this call’s notes" body="Notes follow who can see the call. Ask an admin if you need them." /> : null}
      {list.status === 'unavailable' ? <StatePanel variant="unavailable" compact headingLevel={3} title="Notes aren’t available for this call" body="This server may not have call notes yet, or the call’s notes aren’t shared with you." /> : null}
      {list.status === 'error' ? <StatePanel variant="error" compact headingLevel={3} title="Couldn’t load notes" body={list.message} action={<button type="button" className="btn" onClick={() => setTick(t => t + 1)}>Try again</button>} /> : null}
      {list.status === 'ready' ? (
        list.notes.length === 0 ? <p className="field-hint">No notes yet. Notes explain what happened on a call, including why a number was blocked.</p> : (
          <ul className="note-list" aria-label="Notes on this call">
            {list.notes.map(note => (
              <NoteItem key={note.id} note={note} call={call} client={client} tz={tz} onSaved={saved => replace(list.notes.map(n => (n.id === saved.id ? saved : n)))} onDeleted={id => replace(list.notes.filter(n => n.id !== id))} />
            ))}
          </ul>
        )
      ) : null}
      {list.status === 'ready' || list.status === 'loading' ? (
        <form className="field-group" onSubmit={add} noValidate>
          {carried ? (
            <div className="notice notice-warn" role="status">
              <p>You started this note during the call. It isn’t saved yet.</p>
              <div className="note-actions"><button type="button" className="btn btn-small btn-quiet" onClick={discardCarried}>Discard draft</button></div>
            </div>
          ) : null}
          <div className="field">
            <label htmlFor={`note-new-${call.id}`}>Add a note</label>
            <textarea id={`note-new-${call.id}`} value={text} rows={3} onChange={e => setText(e.target.value.slice(0, NOTE_MAX))} onKeyDown={e => { if (submitKey(e)) void add(); }} aria-describedby={`note-new-hint-${call.id}`} aria-invalid={!!addError || undefined} />
            <p id={`note-new-hint-${call.id}`} className="field-hint">Everyone who can see this call can read its notes. You can change your own note for 15 minutes. Ctrl+Enter adds it.</p>
          </div>
          {addError ? <p className="field-error" role="alert">{addError}</p> : null}
          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={!text.trim() || adding} aria-busy={adding || undefined}><Icon icon={NotebookPen} /> {adding ? 'Adding…' : 'Add note'}</button>
          </div>
        </form>
      ) : null}
    </section>
  );
}

export function NoteItem({ note, call, client, tz, onSaved, onDeleted }: { note: CallNote; call: Call; client: CallNotesClient; tz?: string | undefined; onSaved: (n: CallNote) => void; onDeleted: (id: string) => void }) {
  const ws = useWorkspace();
  const { announce } = useAnnouncer();
  const [mode, setMode] = useState<'view' | 'edit' | 'confirm'>('view');
  const [text, setText] = useState(note.body);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const changeable = canChangeNote(note, ws.userId);
  const editRef = useRef<HTMLTextAreaElement>(null);
  const editButton = useRef<HTMLButtonElement>(null);
  useEffect(() => { if (mode === 'edit') editRef.current?.focus(); }, [mode]);

  const save = async () => {
    const body = text.trim();
    if (!body || busy) return;
    setBusy(true);
    setError(null);
    try {
      const saved = readNote(await client.edit(call.id, note.id, body)) ?? { ...note, body, updated_at: new Date().toISOString() };
      onSaved(saved);
      setMode('view');
      announce('Note saved');
      setTimeout(() => editButton.current?.focus(), 0);
    } catch (e) {
      setError(noteErrorText(e, 'Couldn’t save this note'));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      await client.remove(call.id, note.id);
      announce('Note deleted');
      onDeleted(note.id);
    } catch (e) {
      setError(noteErrorText(e, 'Couldn’t delete this note'));
      setBusy(false);
    }
  };

  const author = note.author.id === ws.userId ? 'You' : note.author.name;
  return (
    <li className="note-item" id={`note-${note.id}`} tabIndex={-1}>
      <p className="meta">
        <strong>{author}</strong>{' '}
        <time dateTime={note.created_at}>{formatAbsolute(note.created_at, tz)}</time>
        {wasEdited(note) ? <>, edited <time dateTime={note.updated_at}>{formatAbsolute(note.updated_at, tz)}</time></> : null}
      </p>
      {mode === 'edit' ? (
        <div className="field">
          <label htmlFor={`note-edit-${note.id}`} className="visually-hidden">Edit note</label>
          <textarea ref={editRef} id={`note-edit-${note.id}`} value={text} rows={3} onChange={e => setText(e.target.value.slice(0, NOTE_MAX))} onKeyDown={e => { if (submitKey(e)) void save(); if (e.key === 'Escape') { e.preventDefault(); setMode('view'); setText(note.body); } }} />
        </div>
      ) : (
        <p className="note-body">{note.body}</p>
      )}
      {mode === 'confirm' ? (
        <div className="note-confirm" role="group" aria-label="Confirm delete">
          <p>Delete this note? The call keeps a record that a note was deleted.</p>
          <div className="note-actions">
            <button type="button" className="btn btn-small btn-end" onClick={() => void remove()} disabled={busy} aria-busy={busy || undefined}><Icon icon={Trash2} /> Delete note</button>
            <button type="button" className="btn btn-small" onClick={() => setMode('view')} autoFocus>Keep note</button>
          </div>
        </div>
      ) : null}
      {error ? <p className="field-error" role="alert">{error}</p> : null}
      {changeable && mode === 'view' ? (
        <div className="note-actions">
          <button ref={editButton} type="button" className="btn btn-small btn-quiet" onClick={() => { setText(note.body); setMode('edit'); }}><Icon icon={Pencil} /> Edit<span className="visually-hidden"> your note</span></button>
          <button type="button" className="btn btn-small btn-quiet" onClick={() => setMode('confirm')}><Icon icon={Trash2} /> Delete<span className="visually-hidden"> your note</span></button>
        </div>
      ) : null}
      {mode === 'edit' ? (
        <div className="note-actions">
          <button type="button" className="btn btn-small btn-primary" onClick={() => void save()} disabled={!text.trim() || busy} aria-busy={busy || undefined}>{busy ? 'Saving…' : 'Save note'}</button>
          <button type="button" className="btn btn-small" onClick={() => { setMode('view'); setText(note.body); }}>Cancel</button>
        </div>
      ) : null}
    </li>
  );
}

/**
 * The note field on the live call card. The text is kept in this tab every 2 seconds and reaches the
 * server only when the person presses Save note. If the call ends first, the call's page offers it.
 */
export function CallNoteField({ call }: { call: Call }) {
  const ws = useWorkspace();
  const { announce } = useAnnouncer();
  const client = useMemo(() => new CallNotesClient(ws.api), [ws.api]);
  const drafts = useMemo(() => sessionDrafts(), []);
  const key = draftKey(ws.session.tenant?.id, ws.userId, call.id);
  const [text, setText] = useState(() => drafts.get(key) ?? '');
  const [state, setState] = useState<{ kind: 'idle' | 'saving' | 'saved' | 'error'; at?: number; message?: string; count: number }>({ kind: 'idle', count: 0 });
  const latest = useRef(text);
  latest.current = text;

  useEffect(() => {
    const t = setTimeout(() => (text ? drafts.set(key, text) : drafts.remove(key)), DRAFT_SAVE_MS);
    return () => clearTimeout(t);
  }, [drafts, key, text]);
  // The card closes when the call ends: keep whatever was typed since the last autosave.
  useEffect(() => () => { if (latest.current) drafts.set(key, latest.current); else drafts.remove(key); }, [drafts, key]);

  const save = async () => {
    const body = text.trim();
    if (!body || state.kind === 'saving') return;
    setState(s => ({ ...s, kind: 'saving' }));
    try {
      await client.add(call.id, body);
      latest.current = '';
      setText('');
      drafts.remove(key);
      setState(s => ({ kind: 'saved', at: Date.now(), count: s.count + 1 }));
      announce('Note saved');
    } catch (error) {
      setState(s => ({ ...s, kind: 'error', message: noteErrorText(error, 'Couldn’t save this note') }));
    }
  };

  const id = `call-note-${call.id}`;
  const status = state.kind === 'saving' ? 'Saving…'
    : state.kind === 'error' ? `${state.message} Your text is still here.`
    : text.trim() ? (live(call) ? 'Not saved yet. If the call ends first, finish it on the call’s page.' : 'Not saved yet. Save it here, or finish it on the call’s page.')
    : state.kind === 'saved' && state.at ? `Saved at ${formatClock(state.at)}. ${state.count === 1 ? 'One note' : `${state.count} notes`} added on this call.`
    : 'Everyone who can see this call can read its notes.';
  return (
    <div className="call-note">
      <BlocklistInlineStyles />
      <div className="field">
        <label htmlFor={id}>Note</label>
        <textarea id={id} value={text} rows={3} onChange={e => setText(e.target.value.slice(0, NOTE_MAX))} onKeyDown={e => { if (submitKey(e)) void save(); }} aria-describedby={`${id}-status`} />
      </div>
      <div className="call-note-foot">
        <p id={`${id}-status`} className={state.kind === 'error' ? 'field-error' : 'meta'} role={state.kind === 'error' ? 'alert' : 'status'}>{status}</p>
        <button type="button" className="btn btn-small" onClick={() => void save()} disabled={!text.trim() || state.kind === 'saving'} aria-busy={state.kind === 'saving' || undefined}>
          <Icon icon={NotebookPen} /> Save note
        </button>
      </div>
    </div>
  );
}
